import {
  Menu,
  MenuButton,
  Button,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Box,
  Text,
  // theme,
  Icon,
  // Show,
} from '@everlywell/ui-kit';
import { CaretDown } from '@phosphor-icons/react';
import React, { ReactNode, useState } from 'react';

export type DropdownFilterProps = {
  /**  Label for the button that opens the dropdown */
  buttonLabel: string;
  /** List of options to display in the dropdown */
  options: string[];
  /** Callback function to handle when an option is selected.
   * Returns an array of selected options.
   */
  onChange: (values: string[]) => void;
  /** Optional default label for the dropdown button.
   * Shows after the button label on initial load,
   * if the defaultSelectedOptions is not provided
   * */
  defaultSelectedLabel?: string;
  /** Optional list of selected options */
  defaultSelectedOptions?: string[];
  /** Whether to allow multiple selections, defaults to false */
  allowMultiple?: boolean;
};

/**
 * DropdownFilter component
 * Used to display a dropdown filter, with optional multiple selections.
 * Uses the Everlywell UI Kit Menu component.
 *
 * @param {DropdownFilterProps} props
 * @returns {JSX.Element}
 *
 */
export default function DropdownFilter({
  buttonLabel,
  options,
  onChange,
  defaultSelectedLabel,
  defaultSelectedOptions,
  allowMultiple = false,
}: DropdownFilterProps): JSX.Element {
  const menuOptionGroupType = allowMultiple ? 'checkbox' : 'radio';
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(
    new Set(defaultSelectedOptions || []),
  );

  /**
   * Handles multiple selection for a given option.
   *
   * @param option - The option to be selected or deselected.
   */
  const handleMultipleSelection = (option: string) => {
    const newSelectedOptions = new Set(selectedOptions);
    if (selectedOptions.has(option)) {
      newSelectedOptions.delete(option);
    } else {
      newSelectedOptions.add(option);
    }
    setSelectedOptions(newSelectedOptions);
    onChange(Array.from(newSelectedOptions));
  };

  /**
   * Handles single selection for a given option.
   *
   * @param option - The option to be selected or deselected.
   */
  const handleSingleSelect = (option: string) => {
    if (selectedOptions.has(option)) {
      setSelectedOptions(new Set([]));
      onChange([]);
      return;
    }

    setSelectedOptions(new Set([option]));
    onChange([option]);
    return;
  };

  /**
   * Handles the click event for an option, based on whether multiple selections are allowed or not.
   *
   * @param option - The option to be selected or deselected.
   */
  const handleOptionClick = (option: string) => {
    allowMultiple
      ? handleMultipleSelection(option)
      : handleSingleSelect(option);
  };

  /**
   * Retrieves the content for the button text based on the selected options.
   *
   * @returns JSX.Element representing the button text content.
   */
  const getButtonTextContent = (): JSX.Element => {
    if (selectedOptions.size === 0) {
      return (
        <>
          {buttonLabel}
          {/* TODO: Commented for launch, only display the selected category */}
          {/* {defaultSelectedLabel ? `: ${defaultSelectedLabel}` : ''} */}
        </>
      );
    }

    if (selectedOptions.size > 0 && selectedOptions.size < 3) {
      return (
        <>
          {/* Show counter for tablet and below */}
          {/* TODO: Commented for launch, only display the selected category */}
          {/* <Show breakpoint={`(max-width: ${theme.breakpoints.tablet})`}>
            <Counter>{selectedOptions.size}</Counter>{' '}
          </Show> */}
          {selectedOptions.size ? selectedOptions : buttonLabel}
          {/* Show label for above tablet */}
          {/* TODO: Commented for launch, only display the selected category */}
          {/* <Show breakpoint={`(min-width: ${theme.breakpoints.tablet})`}>
            : {Array.from(selectedOptions).join(', ')}
          </Show> */}
        </>
      );
    }

    // When 2 or more options are selected, show the counter
    return (
      <>
        <Counter>{selectedOptions.size}</Counter> {buttonLabel}
      </>
    );
  };

  return (
    <Box display="flex" width="100%">
      {/* When allow multiple is false, the dropdown should close on select as it will render a radio button */}
      <Menu closeOnSelect={!allowMultiple} size={['lg', 'md']}>
        <MenuButton
          as={Button}
          size={['md', 'sm']}
          colorScheme="whiteCS"
          borderRadius="12"
          border="1.5px solid"
          borderColor="tints.lightGrey"
          width="100%"
        >
          <Box display="flex" justifyContent={['space-between', 'center']}>
            <Box display="flex" alignItems="center" color="tints.darkGrey">
              {getButtonTextContent()}
            </Box>
            <Icon as={CaretDown} marginLeft="2" color="tints.darkGrey"></Icon>
          </Box>
        </MenuButton>
        <MenuList zIndex={2}>
          <MenuOptionGroup
            defaultValue={defaultSelectedOptions}
            type={menuOptionGroupType}
          >
            {options.map((option) => (
              <MenuItemOption
                key={option}
                value={option}
                onClick={() => handleOptionClick(option)}
                icon={
                  selectedOptions.has(option) ? undefined : (
                    /* This is the default behavior from chalk-ui when the item is not selected. 
                    This a way of making the single option filter unselectable */
                    <svg width="1rem" height="1rem"></svg>
                  )
                }
              >
                {option}
              </MenuItemOption>
            ))}
          </MenuOptionGroup>
        </MenuList>
      </Menu>
    </Box>
  );
}

// TODO: Use Tag component from UI Kit when it is available
const Counter = ({ children }: { children: ReactNode }) => (
  <Text
    display="inline-block"
    backgroundColor="viridian.base"
    width="20px"
    height="20px"
    borderRadius="50%"
    color="white"
    marginRight="1"
    fontSize="md"
  >
    {children}
  </Text>
);
